import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, CheckCheck } from "lucide-react";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function timeAgo(iso) {
  try {
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
  } catch { return ""; }
}

export function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/community/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* not a community member yet — ignore */ }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 30000);
    return () => clearInterval(timer.current);
  }, [load]);

  const markAll = async () => {
    await api.post("/community/notifications/read");
    setUnread(0);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) load();
  };

  const goTo = (n) => {
    setOpen(false);
    nav("/community");
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell-btn"
          aria-label="Notifications"
          className="relative rounded-sm p-2 text-zinc-300 transition-colors hover:text-[#DCA846]"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#DCA846] px-1 text-[10px] font-bold leading-none text-black"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="notification-dropdown"
        className="w-[calc(100vw-2rem)] max-w-sm border-white/10 bg-zinc-950 p-0 text-white sm:w-96"
      >
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <span className="font-display text-sm uppercase tracking-wide">Notifications</span>
          {items.some((n) => !n.read) && (
            <button
              data-testid="notif-mark-all-read"
              onClick={markAll}
              className="inline-flex items-center gap-1 text-xs text-[#DCA846] hover:underline"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500">
              <Bell className="mx-auto mb-2 h-6 w-6 text-zinc-600" />
              No activity yet. When barbers support your posts, you'll see it here.
            </div>
          ) : (
            items.slice(0, 12).map((n) => (
              <button
                key={n.id}
                data-testid={`bell-notif-${n.id}`}
                onClick={() => goTo(n)}
                className={`flex w-full items-center gap-3 border-b border-white/5 px-4 py-3 text-left transition-colors hover:bg-white/5 ${n.read ? "" : "bg-[#DCA846]/5"}`}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#DCA846]/30 text-[#DCA846]">
                  <Heart className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-zinc-200">{n.message}</p>
                  <p className="text-xs text-zinc-500">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-[#DCA846]" />}
              </button>
            ))
          )}
        </div>

        <button
          data-testid="notif-view-all"
          onClick={() => { setOpen(false); nav("/community"); }}
          className="block w-full border-t border-white/10 px-4 py-3 text-center text-xs uppercase tracking-wide text-[#DCA846] hover:bg-white/5"
        >
          View all activity
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
